import { clsx } from 'clsx';
import { Col, Row } from 'antd';
import { RcFile } from 'antd/lib/upload';
import { SaveFilled } from '@ant-design/icons';
import { useFormContext } from 'react-hook-form';
import React, { useEffect, useState } from 'react';
import {
  DeleteButton,
  SaveButton,
  SimpleForm,
  TopToolbar,
  useRedirect,
  useResourceContext,
  useRecordContext, useTheme,
} from 'react-admin';

import {
  Container,
  convertFileToBase64,
  EditorInput,
  FileUpload,
  FormRow, InputSelectAutocomplete,
  LinkInputAlter,
  LoadAutocompleteInput,
  ManyInput, ManyTagsInput,
  MoneyInput,
  NumberInput,
  OneInput,
  PageHeader,
  SwitchInput,
  TextInput,
} from '@components';
import { showAxiosError } from '@utils';
import { AdminOptionCats } from '@types';
import { adminSettingsHttp } from '@network';
import cls from './ProductForm.module.css';
import { useSettingsContext } from '@providers';


const urlRegex =
  /^(https?:\/\/)?([^\s:@]+(:[^\s:@]*)?@)?([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(:\d{2,5})?(\/[^\s]*)?$/;


interface ActionsProps {
  resource: string;
  canEdit?: boolean;
  editPage?: boolean;
}

interface FormProps {
  canEdit?: boolean;
  editPage?: boolean;
}

interface Option {
  value: number;
  name: string;
}

const editorToolbar = 'undo redo formatselect '
  + 'bold italic backcolor bullist numlist outdent indent table '
  + 'removeformat code help '
  + 'alignleft aligncenter alignright alignjustify ';
const MAX_IMAGE_SIZE = 5;

export const productValidate = (values: any) => {
  const errors: any = {};

  ['name', 'divisionId', 'discount', 'pdfLink']
    .forEach((key: string) => {
      if (key === 'discount') {
        if ((values[key] !== undefined && (+values[key] < 0 || +values[key] > 100))) {
          errors[key] = 'This field has invalid numeric value';
        }
      } else if (key === 'pdfLink') {
        if (values[key] && !urlRegex.test(values[key])) {
          errors[key] = 'This field has invalid URL value';
        }
      } else if (key !== 'pdfLink' && !values[key]) {
        errors[key] = 'This field is required';
      }
    });

  return errors;
};

const LinkIcon = ({ className }: { className?: string; }) => {
  return (
    <svg
      width="38"
      height="38"
      viewBox="0 0 38 38"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <circle cx="19" cy="19" r="19" fill="var(--primary-color)" />
      <g clipPath="url(#clip0_320_7)">
        <path
          d="M19 5C11.3 5 5 11.3 5 19C5 26.7 11.3 33 19 33C26.7 33 33 26.7 33 19C33 11.3 26.7 5 19 5ZM31.0867 18.0667H25.3C25.2533 16.2933 25.0667 14.6133 24.7867 13.12C26.3267 12.84 27.68 12.42 28.8467 11.9067C30.1067 13.68 30.9 15.78 31.0867 18.0667ZM27.54 10.4133C26.6067 10.7867 25.5333 11.0667 24.32 11.3C23.9 9.76 23.2933 8.45333 22.64 7.42667C24.5067 8.03333 26.1867 9.06 27.54 10.4133ZM14.5667 18.0667C14.6133 16.34 14.8 14.8 15.08 13.4C16.2933 13.54 17.6 13.6333 19 13.6333C20.4 13.6333 21.66 13.54 22.92 13.4C23.2 14.8 23.3867 16.34 23.4333 18.0667H14.5667ZM23.4333 19.9333C23.3867 21.66 23.2 23.2 22.92 24.6C21.7067 24.46 20.4 24.3667 19 24.3667C17.6 24.3667 16.34 24.46 15.08 24.6C14.8 23.2 14.6133 21.66 14.5667 19.9333H23.4333ZM19 6.86667C20.26 6.86667 21.5667 8.64 22.4533 11.58C21.38 11.72 20.2133 11.7667 19 11.7667C17.7867 11.7667 16.62 11.6733 15.5467 11.58C16.4333 8.64 17.74 6.86667 19 6.86667ZM15.36 7.42667C14.7067 8.45333 14.1 9.76 13.68 11.3C12.4667 11.0667 11.3933 10.7867 10.46 10.4133C11.8133 9.06 13.4933 8.03333 15.36 7.42667ZM9.15333 11.9067C10.32 12.42 11.6733 12.84 13.2133 13.12C12.9333 14.6133 12.7467 16.2933 12.7 18.0667H6.91333C7.1 15.78 7.89333 13.68 9.15333 11.9067ZM6.91333 19.9333H12.7C12.7467 21.7067 12.9333 23.3867 13.2133 24.88C11.6733 25.16 10.32 25.58 9.15333 26.0933C7.89333 24.3667 7.1 22.22 6.91333 19.9333ZM10.46 27.5867C11.3933 27.2133 12.4667 26.9333 13.68 26.7C14.1 28.24 14.7067 29.5467 15.36 30.5733C13.4933 29.9667 11.8133 28.94 10.46 27.5867ZM19 31.1333C17.74 31.1333 16.4333 29.36 15.5467 26.42C16.62 26.28 17.7867 26.2333 19 26.2333C20.2133 26.2333 21.38 26.3267 22.4533 26.42C21.5667 29.36 20.26 31.1333 19 31.1333ZM22.64 30.5733C23.2933 29.5467 23.9 28.24 24.32 26.7C25.5333 26.9333 26.6067 27.2133 27.54 27.5867C26.1867 28.94 24.5067 29.9667 22.64 30.5733ZM28.8467 26.0933C27.68 25.58 26.3267 25.16 24.7867 24.88C25.0667 23.3867 25.2533 21.7067 25.3 19.9333H31.0867C30.9 22.22 30.1067 24.32 28.8467 26.0933Z"
          fill="white"/>
      </g>
      <defs>
        <clipPath id="clip0_320_7">
          <rect width="28" height="28" fill="white" transform="translate(5 5)"/>
        </clipPath>
      </defs>
    </svg>
  );
};

const Actions = ({ resource, canEdit, editPage }: ActionsProps) => {
  const redirect = useRedirect();

  return (
    <TopToolbar className={cls.actions}>
      {!editPage || canEdit ? (
        <SaveButton
          icon={<SaveFilled className={cls.actionIcon} />}
          label="Save"
          type="button"
          className={clsx(cls.action, cls.actionSave)}
          variant="outlined"
          alwaysEnable
          mutationOptions={{
            onSuccess: (record) => {
              redirect(`/${resource}/${record.id}`);
            },
          }}
        />) : null}
      {editPage && canEdit ? <DeleteButton
        className={clsx(cls.action, cls.actionDelete)}
      /> : null}
    </TopToolbar>
  );
};

const UploadContent = () => {
  return (
    <div className={cls.uploadContent}>
      <div className={cls.uploadContentBlock}>
        <div className={cls.uploadContentTitle}>Upload Image</div>
        <div className={cls.uploadContentIcon}>
          <svg xmlns="http://www.w3.org/2000/svg"
            width="26"
            height="32"
            viewBox="0 0 26 32"
            fill="none">
            <path d="M21.5 15.27C21.5 13.34 19.93 11.77 18 11.77C16.07 11.77 14.5 13.34 14.5 15.27C14.5 17.2 16.07 18.77 18 18.77C19.93 18.77 21.5 17.2 21.5 15.27ZM18 13.77C18.83 13.77 19.5 14.44 19.5 15.27C19.5 16.1 18.83 16.77 18 16.77C17.17 16.77 16.5 16.1 16.5 15.27C16.5 14.44 17.17 13.77 18 13.77Z"
              fill="#042E6B" />
            <path d="M23 8.27002H17C16.45 8.27002 16 8.72002 16 9.27002C16 9.82002 16.45 10.27 17 10.27H23C23.55 10.27 24 10.72 24 11.27V25.48L20.08 21.56C18.32 19.89 15.54 19.93 13.83 21.65L12.73 22.75L11.82 21.83L8.1 18.14C6.5 16.56 4.19 15.91 2 16.43V11.27C2 10.72 2.45 10.27 3 10.27H9C9.55 10.27 10 9.82002 10 9.27002C10 8.72002 9.55 8.27002 9 8.27002H3C1.34 8.27002 0 9.61002 0 11.27V28.27C0 29.93 1.34 31.27 3 31.27H22C24.21 31.27 26 29.48 26 27.27V11.27C26 9.61002 24.66 8.27002 23 8.27002ZM22 29.27H3C2.45 29.27 2 28.82 2 28.27V18.53L2.27 18.41C3.84 17.97 5.53 18.41 6.68 19.56L10.39 23.27L11.39 24.27H11.44L11.59 24.42C11.9 24.69 12.31 24.83 12.72 24.83C13.12 24.83 13.51 24.69 13.82 24.45L14 24.27L15.22 23.05C16.21 22.11 17.77 22.11 18.76 23.05L23.76 28.05C23.46 28.76 22.77 29.24 22 29.27Z"
              fill="#042E6B" />
            <path d="M10.5209 4.69986L12.0009 3.26986V11.2699C12.0009 11.8199 12.4509 12.2699 13.0009 12.2699C13.5509 12.2699 14.0009 11.8199 14.0009 11.2699V3.19986L15.4809 4.67986C15.8709 5.06986 16.5109 5.06986 16.9009 4.67986C17.2909 4.28986 17.2909 3.65986 16.9009 3.26986L14.0709 0.449864C13.5109 -0.120136 12.5909 -0.150136 12.0009 0.389864L11.9309 0.449864L9.11086 3.26986C8.72086 3.65986 8.72086 4.29986 9.11086 4.68986C9.50086 5.07986 10.1309 5.08986 10.5309 4.69986H10.5209Z"
              fill="#042E6B" />
          </svg>
        </div>
        <div className={cls.uploadContentText}>1600x630px</div>
      </div>
      <div className={cls.uploadContentComment}>
        *File size should not exceed {MAX_IMAGE_SIZE}MB
      </div>
    </div>
  );
};

const InnerFormContent = () => {
  const [currentTheme] = useTheme();
  const { isProductPackActive } = useSettingsContext();
  const { setValue, watch, register } = useFormContext();
  const record = useRecordContext();
  const [image, setImage] = useState<string | null>(watch('image') || null);
  const mainColGrid = {
    xxl: { title: 7, children: 17 },
    sm: { title: 10, children: 14 },
    xs: { title: 24, children: 24 },
  };
  const [formOptions, setFormOptions] = useState<Option[]>([]);
  const [packOptions, setPackOptions] = useState<Option[]>([]);

  const uploadPicture = async (rcFile: RcFile): Promise<any> => {
    try {
      const base64Img: string = await convertFileToBase64(rcFile) as string;

      if (base64Img) {
        setImage(base64Img);
        setValue('image', base64Img, { shouldDirty: true, shouldValidate: true });
      }
    } catch (err: any) {
      showAxiosError(err);
    }
  };

  const onImageClear = () => {
    setImage(null);
    setValue('image', null, { shouldDirty: true, shouldValidate: true });
  };

  const onChangeFormId = (value: number) => {
    setValue('formId', value);
  };

  const onChangePackIds = (value: number[]) => {
    setValue('packIds', value);
  };

  useEffect(() => {
    register('image');
  }, [register]);

  useEffect(() => {

    (async () => {
      const [
        formOptions,
        packOptions,
      ] = await Promise.all([
        adminSettingsHttp.getOptionsByCategory(AdminOptionCats.PRODUCT_PRESENTATION),
        adminSettingsHttp.getOptionsByCategory(AdminOptionCats.PRODUCT_PACK_SIZE),
      ]);

      setFormOptions(formOptions.map(i => ({
        value: i.id as number,
        name: i.name,
      })));
      setPackOptions(packOptions.map(i => ({
        value: i.id as number,
        name: i.name,
      })));
    })();
  }, []);

  return (
    <>
      <Container max>
        <Row gutter={[24, 24]} className={cls.wrapper}>
          <Col xs={24} xl={12} className={cls.wrapperCol}>
            <div className={clsx(cls.block, cls.blockFullHeight, cls.mb3)}>
              <div className={cls.blockHeader}>Information of product</div>
              <div className={cls.blockMiddleInfo}>
                <FormRow
                  title="Product Name*:"
                  width={mainColGrid}
                  rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                  colTitleClassName={cls.formLabelCol}
                  colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                >
                  <TextInput
                    source="name"
                    label={false}
                    required
                    inputProps={{ maxLength: 100 }} />
                </FormRow>
                <FormRow
                  title="Brand:"
                  width={mainColGrid}
                  rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                  colTitleClassName={cls.formLabelCol}
                  colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                >
                  <OneInput source="brandId" reference="product-brand" hideLabel />
                </FormRow>
                <FormRow
                  title="Category*:"
                  width={mainColGrid}
                  rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                  colTitleClassName={cls.formLabelCol}
                  colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                >
                  <OneInput source="divisionId" reference="division" hideLabel required />
                </FormRow>
                <FormRow
                  title="Subcategory:"
                  width={mainColGrid}
                  rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                  colTitleClassName={cls.formLabelCol}
                  colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                >
                  <OneInput source="typeId" reference="product-type" hideLabel />
                </FormRow>
                <FormRow
                  title="Application Type:"
                  width={mainColGrid}
                  rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                  colTitleClassName={cls.formLabelCol}
                  colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue, cls.acInput)}
                >
                  <LoadAutocompleteInput source="application" hideLabel />
                </FormRow>
                <FormRow
                  title="Dose Per Ha:"
                  width={mainColGrid}
                  rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                  colTitleClassName={cls.formLabelCol}
                  colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue, cls.acInput)}
                >
                  <LoadAutocompleteInput source="dosePerHa" hideLabel />
                </FormRow>
                <FormRow
                  title="Presentation:"
                  width={mainColGrid}
                  rowClassName={clsx(cls.formRow, cls.mainFormRow, cls.formIdField)}
                  colTitleClassName={cls.formLabelCol}
                  colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue, cls.acInput)}
                >
                  <InputSelectAutocomplete
                    label=""
                    onChange={onChangeFormId}
                    options={formOptions.map(item => ({
                      value: item.value,
                      label: item.name,
                    }))}
                    selectedValue={watch('formId')}
                    displayEmpty
                  />
                </FormRow>
                {isProductPackActive ? (
                  <>
                    <FormRow
                      title="Pack Size:"
                      width={mainColGrid}
                      rowClassName={clsx(cls.formRow, cls.mainFormRow, cls.packField)}
                      colTitleClassName={cls.formLabelCol}
                      colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue, cls.acInput)}
                    >
                      <ManyTagsInput
                        source="packIds"
                        params={{ category: 'product_pack_size', nolist: true }}
                        reference="admin-settings-options"/>
                    </FormRow>
                    <FormRow
                      title="Pack Type:"
                      width={mainColGrid}
                      rowClassName={clsx(cls.formRow, cls.mainFormRow, cls.packField)}
                      colTitleClassName={cls.formLabelCol}
                      colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue, cls.acInput)}
                    >
                      <ManyTagsInput
                        source="packTypesIds"
                        params={{ category: 'product_pack_type', nolist: true }}
                        reference="admin-settings-options"/>
                    </FormRow>
                  </>
                ) : null}
                <FormRow
                  title="Origin:"
                  width={mainColGrid}
                  rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                  colTitleClassName={cls.formLabelCol}
                  colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                >
                  <div className={cls.originInput}>
                    {record ? record.origin ?
                      record.origin.toLowerCase().replace(/^./, (c) => c.toUpperCase())
                      : '' : 'Direct'}
                  </div>
                </FormRow>
                <FormRow
                  title="Price:"
                  width={mainColGrid}
                  rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                  colTitleClassName={cls.formLabelCol}
                  colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                  prefix={'$'}
                >
                  <MoneyInput source="price" label={false} max={999999} min={0} />
                </FormRow>
                <FormRow
                  title="Discount:"
                  width={mainColGrid}
                  rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                  colTitleClassName={cls.formLabelCol}
                  colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                  prefix={'%'}
                >
                  <NumberInput max={100} source="discount" label={false} />
                </FormRow>
                <div className={cls.checkboxRow}>
                  <Row gutter={[24, 24]}>
                    <Col xs={24}>
                      <SwitchInput
                        name="isExternal"
                        source="isExternal"
                        label="For External Marketplace"
                        defaultValue={true}
                        className={cls.switcherField}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={6} xl={12}>
                      <SwitchInput
                        name="isEnabled"
                        source="isEnabled"
                        label="In Marketplace"
                        defaultValue={false}
                        className={cls.switcherField}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={6} xl={12}>
                      <SwitchInput
                        name="isNew"
                        source="isNew"
                        label="New product label"
                        defaultValue={false}
                        className={cls.switcherField}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={6} xl={12}>
                      <SwitchInput
                        name="allCrops"
                        source="allCrops"
                        label="For All Crops"
                        defaultValue={false}
                        className={cls.switcherField}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={6} xl={12}>
                      <SwitchInput
                        name="showPrice"
                        source="showPrice"
                        label="Show Price"
                        defaultValue={false}
                        className={cls.switcherField}
                      />
                    </Col>
                  </Row>
                </div>
                <FormRow
                  title="Specific Crops:"
                  width={mainColGrid}
                  rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                  colTitleClassName={cls.formLabelCol}
                  colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                >
                  <ManyInput source="crops" reference="crop" hideLabel />
                </FormRow>
              </div>
            </div>
          </Col>
          <Col xs={24} xl={12} className={cls.wrapperCol}>
            <div className={clsx(cls.block, cls.blockFullHeight, cls.mb3)}>
              <div className={cls.blockHeader}>Description of product</div>
              <div className={cls.blockMiddleInfo}>
                <FormRow
                  title="Composition:"
                  fullWidth
                  rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                  colTitleClassName={clsx(cls.formLabelCol, cls.fullTitle)}
                  colChildrenClassName={clsx(
                    cls.formValueCol, cls.formMainMultilineValue, cls.kingSize,
                  )}
                >
                  <TextInput
                    source="composition"
                    name="composition"
                    placeholder="Write the components in list"
                    inputProps={{ maxLength: 900 }}
                    label={false}
                    multiline
                    fullWidth
                  />
                </FormRow>
                <FormRow
                  title="Description:"
                  fullWidth
                  rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                  colTitleClassName={clsx(cls.formLabelCol, cls.fullTitle)}
                  colChildrenClassName={clsx(cls.formValueCol, cls.formMainMultilineValue)}
                >
                  <TextInput
                    source="description"
                    name="description"
                    placeholder="Write a description in 160 characteres"
                    inputProps={{ maxLength: 160 }}
                    label={false}
                    multiline
                    fullWidth
                  />
                </FormRow>
                <div className={cls.subtitle}>Image</div>
                {image ? (
                  <div className={cls.uploadedBox} onClick={onImageClear}>
                    <img src={image} alt="Img" className={cls.image} />
                  </div>
                ) : (
                  <FileUpload
                    uploadAction={uploadPicture}
                    className={cls.uploadBox}
                    type="image"
                    content={<UploadContent />}
                    size={MAX_IMAGE_SIZE}
                  />
                )}
              </div>
            </div>
          </Col>
          <Col xs={24} className={cls.wrapperCol}>
            <div className={clsx(cls.block)}>
              <div className={cls.blockHeader}>Other Data</div>
              <div className={cls.blockMiddleInfo}>
                <FormRow
                  title="PDF link:"
                  fullWidth
                  rowClassName={clsx(cls.formRow, cls.linkFormRow)}
                  colTitleClassName={clsx(cls.formLabelCol, cls.linkLabel)}
                  colChildrenClassName={clsx(cls.formValueCol, cls.formLinkValue)}
                >
                  <LinkInputAlter
                    source="pdfLink"
                    name="pdfLink"
                    label={false}
                    placeholder="http://any.link.pdf"
                    leftIcon={<LinkIcon className={cls.linkIcon}/>}
                    inputProps={{ maxLength: 160 }}
                  />
                </FormRow>
                <FormRow
                  title="Keywords:"
                  fullWidth
                  rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                  colTitleClassName={clsx(cls.formLabelCol, cls.fullTitle)}
                  colChildrenClassName={clsx(
                    cls.formValueCol, cls.formMainMultilineValue, cls.littleSize,
                  )}
                >
                  <TextInput
                    source="keywords"
                    name="keywords"
                    placeholder="Enter the key words"
                    label={false}
                    multiline
                    fullWidth
                  />
                </FormRow>
                <FormRow
                  fullWidth
                  title="Recommendations:"
                  rowClassName={clsx(cls.formRow, cls.messageRow)}
                  colTitleClassName={clsx(cls.formLabelCol, cls.formTitleCol, cls.fullTitle)}
                  colChildrenClassName={clsx(cls.formValueCol, cls.formValueColTextarea)}
                >
                  <EditorInput
                    theme={String(currentTheme)}
                    source="recommendations"
                    label={false}
                    toolbar={editorToolbar}
                    height={300}
                    className={clsx(cls.editor)}
                    required
                  />
                </FormRow>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export const ProductForm = ({ canEdit, editPage }: FormProps) => {
  const resource = useResourceContext();

  return (
    <div className={cls.wrap}>
      <PageHeader
        title="Products's information"
        linkText="Back to all records"
        linkUrl={'/product'}
      />
      <SimpleForm
        className={clsx(cls.form, 'manager-form')}
        toolbar={<Actions
          resource={resource}
          canEdit={canEdit}
          editPage={editPage}
        />}
        validate={productValidate}
      >
        <InnerFormContent />
      </SimpleForm>
    </div>
  );
};
